import React, { useContext, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import { useQuery } from "react-query";

import OnlinePredictionOutlinedIcon from "@mui/icons-material/OnlinePredictionOutlined";

import PageHeader from "../../../components/PageHeader";
import SectionCard from "../../../components/SectionCard";
import DataTable from "../../../components/DataTable";
import CalculationTabs from "../../../components/CalculationTabs";
import { getForecast, getForecastDates } from "../../../api/forecastApi";
import { LanguageContext } from "../../../components/LanguageContext";

const ForecastPage = () => {
    const { excelId } = useParams();
    const { translations } = useContext(LanguageContext) || {};

    const { data: data1, isLoading: isLoading1 } = useQuery({
        queryKey: ["forecast", excelId],
        queryFn: () => getForecast(excelId),
    });

    const { data: data2, isLoading: isLoading2 } = useQuery({
        queryKey: ["dates", excelId],
        queryFn: () => getForecastDates(excelId),
    });

    const dates = useMemo(() => data2?.data || [], [data2]);

    const rows = useMemo(() => {
        if (isLoading1 || !data1?.data) return [];
        return data1.data.map((d) => {
            const row = { id: d.id, code: d.code, name: d.name };
            const arr = JSON.parse(d.forecast);
            arr.forEach((item) => {
                row[item.date] = parseFloat(item.amount).toFixed(2);
            });
            return row;
        });
    }, [data1, isLoading1]);

    const columns = useMemo(
        () => [
            {
                field: "code",
                headerName:
                    translations?.adminExcelProInfoPageProductCode ||
                    "Šifra proizvoda",
                minWidth: 140,
                renderCell: (params) => (
                    <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace", fontWeight: 600 }}
                    >
                        {params.value}
                    </Typography>
                ),
            },
            {
                field: "name",
                headerName:
                    translations?.adminExcelProInfoPageProductName ||
                    "Naziv proizvoda",
                minWidth: 220,
                flex: 2,
                renderCell: (params) => (
                    <Typography
                        sx={{
                            fontWeight: 600,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                        title={params.value}
                    >
                        {params.value}
                    </Typography>
                ),
            },
            ...dates.map((d) => ({
                field: `${d}`,
                headerName: d,
                width: 120,
                align: "center",
                headerAlign: "center",
                renderCell: (params) => (
                    <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace" }}
                    >
                        {params.value ?? "—"}
                    </Typography>
                ),
            })),
        ],
        [dates, translations]
    );

    return (
        <Box>
            <PageHeader
                icon={<OnlinePredictionOutlinedIcon />}
                eyebrow={
                    translations?.adminExcelProInfoPageForecast || "Forecast"
                }
                title={
                    translations?.adminForecastPageTitle ||
                    "Predviđanje potražnje"
                }
                description={
                    translations?.adminForecastPageSubtitle ||
                    "Prognoza tražnje proizvoda po mesecima."
                }
            />

            <CalculationTabs active="informacije" />

            <SectionCard padded={false}>
                <DataTable
                    rows={rows}
                    columns={columns}
                    loading={isLoading1 || isLoading2}
                    rowHeight={56}
                    emptyIcon={<OnlinePredictionOutlinedIcon />}
                    emptyTitle={
                        translations?.messageIfEmptyData ||
                        "Nema podataka za prikaz"
                    }
                />
            </SectionCard>
        </Box>
    );
};

export default ForecastPage;